/**
 * Lazy-loaded icons with encapsulated Suspense boundaries.
 */
import React, { Suspense } from 'react';

// oxlint-disable-next-line @atlassian/no-restricted-imports
import { lazyForPaint } from '@atlassian/react-loosely-lazy';

import { withBrandIcon, type BrandIcon } from '../../../icons/with-brand-icon';

const OfficialIcon = lazyForPaint(
	() =>
		import(
			/* webpackChunkName: "@atlassian/logo-third-party/docusign" */ '@atlassian/logo-third-party/docusign'
		).then((m) => m.DocuSignIcon),
	{ moduleId: '@atlassian/logo-third-party/docusign' },
);

const LazyDocusignIconComponent = lazyForPaint(
	() => import(/* webpackChunkName: "icon-docusign" */ './utils').then((m) => m.DocusignIcon),
	{ moduleId: '@atlassian/rovo-connectors-config/docusign/icon' },
);

const LazyDocusignSearchResultIconComponent = lazyForPaint(
	() =>
		import(/* webpackChunkName: "icon-docusign-search-result" */ './utils').then(
			(m) => m.DocusignSearchResultIcon,
		),
	{ moduleId: '@atlassian/rovo-connectors-config/docusign/search-result-icon' },
);

const LazyDocusignIconBorderlessComponent = lazyForPaint(
	() =>
		import(/* webpackChunkName: "icon-docusign-borderless" */ './utils').then(
			(m) => m.DocusignIconBorderless,
		),
	{ moduleId: '@atlassian/rovo-connectors-config/docusign/icon-borderless' },
);

/**
 * Exported icons with self-contained Suspense boundaries.
 * Consumers can use these directly without wrapping in LazySuspense.
 */
export const DocusignIcon: BrandIcon = withBrandIcon(
	(): React.JSX.Element => (
		<Suspense fallback={null}>
			<LazyDocusignIconComponent />
		</Suspense>
	),
	OfficialIcon,
	'small',
);

export const DocusignSearchResultIcon: BrandIcon = withBrandIcon(
	(): React.JSX.Element => (
		<Suspense fallback={null}>
			<LazyDocusignSearchResultIconComponent />
		</Suspense>
	),
	OfficialIcon,
	'medium',
);

export const DocusignIconBorderless: BrandIcon = withBrandIcon(
	(): React.JSX.Element => (
		<Suspense fallback={null}>
			<LazyDocusignIconBorderlessComponent />
		</Suspense>
	),
	OfficialIcon,
	'xxsmall',
);
